import type { SettingsFlow, UpdateSettingsFlowBody } from '@ory/client';
import { oryKratosClient } from '../ory-kratos.client';
import { handleOryErrorWithReauth } from './error-handler';
import { getErrorResponseData, is400Error, isFlowExpired, type ServiceResponse } from './types';
export const passwordService = {
  /**
   * 設定フローを初期化（パスワード更新など）
   *
   * @param options - オプション
   * @param options.returnTo - 設定完了後のリダイレクト先URL
   * @returns 設定フロー情報
   *
   * @example
   * ```ts
   * const flow = await passwordService.initFlow();
   * // flow.ui を使ってフォームを表示
   * ```
   */
  async initFlow(options?: {
    returnTo?: string;
  }): Promise<SettingsFlow> {
    const {
      data
    } = await oryKratosClient.getApi().createBrowserSettingsFlow({
      returnTo: options?.returnTo
    });
    if (!data) {
      throw new Error('Failed to create settings flow');
    }
    return data;
  },
  /**
   * 設定フローを送信（パスワード更新実行など）
   *
   * @param flowId - 設定フローID
   * @param body - 設定情報（パスワードなど）
   * @returns 設定更新結果。400エラー（バリデーションエラー）の場合は新しいフローデータを返す
   *
   * @example
   * ```ts
   * const result = await passwordService.submitFlow(flowId, {
   *   method: 'password',
   *   password: 'newPassword123',
   *   traits: {
   *     email: 'user@example.com'
   *   }
   * });
   *
   * if (result.error) {
   *   // 予期せぬエラー（500等）
   *   console.error(result.error);
   * } else if (result.data) {
   *   // 成功またはバリデーションエラー（400）
   *   // result.data.ui を使ってエラーメッセージを表示
   * }
   * ```
   */
  async submitFlow(flowId: string, body: UpdateSettingsFlowBody): Promise<ServiceResponse<SettingsFlow>> {
    try {
      // 403エラーで再認証が必要な場合、handleOryErrorWithReauthが自動的に処理
      const {
        data
      } = await handleOryErrorWithReauth(async () => {
        return await oryKratosClient.getApi().updateSettingsFlow({
          flow: flowId,
          updateSettingsFlowBody: body
        });
      });
      return {
        data: data ?? null,
        error: null
      }; // 成功 (200)
    } catch (err) {
      if (is400Error(err)) {
        // バリデーションエラーは「成功」として扱い、新しいフローデータを返す
        const flowData = getErrorResponseData<SettingsFlow>(err);
        return {
          data: flowData,
          error: null
        };
      }

      // self_service_flow_expired (410): 新しいフローを発行して UI 側で再開できるようにする
      if (isFlowExpired(err)) {
        try {
          const newFlow = await passwordService.initFlow();
          return {
            data: newFlow,
            error: null,
            flowExpired: true
          };
        } catch (reinitError) {
          return {
            data: null,
            error: reinitError as Error
          };
        }
      }

      // それ以外(500等)はエラーとして返す
      return {
        data: null,
        error: err as Error
      };
    }
  }
};